import qs from 'qs';

const BASE = '/assets';
const LINK = '/link';
const CONNECTION = '/connections';
const DATASTREAM = '/datastreams';

import memoize from 'memoizee';

const getData = R.prop('data');

const list = ({ token = '', accountId = '' }) => {
    if (!token && !accountId) {
        return Promise.reject('token not provided in get list');
    }

    let params = {};

    if (token) {
        params = {
            ...params,
            token,
        };
    }
    if (accountId) {
        params = {
            ...params,
            accountId,
        };
    }

    return axios
        .get(`${BASE}`, {
            params,
        })
        .then(getData);
};

const addAssets = ({ assets = [], queryObj = {} }) => {
    if (!queryObj.token) {
        return Promise.reject('token not provided in add assets');
    }

    return axios
        .post(`${BASE}?${qs.stringify(queryObj)}`, {
            assets,
            referer: window.location.href,
        })
        .then(getData);
};

const toggleActive = ({ queryObj = {}, assetId, active = false }) => {
    if (!queryObj.token) {
        return Promise.reject('token not provided in toggle active');
    }

    return axios
        .put(`${BASE}/${assetId}?${qs.stringify(queryObj)}`, {
            active,
        })
        .then(R.propEq('status', 200));
};

const getAssets = (code, spreadSheetId) => {
    if (!code) {
        return Promise.reject('code not provided');
    }
    return axios
        .get(
            `${LINK}/assets?code=${code}${
                spreadSheetId ? `&spreadsheetId=${spreadSheetId}` : ''
            }`
        )
        .then(getData);
};

const facebookEvents = (assetId = '') => {
    return axios.get(`${BASE}/${assetId}/conversions`).then(getData);
};

const getYouTubeVideoSearch = ({ id, nextPageToken, q, accountId }) => {
    return axios
        .get(`${BASE}/youtubeChannel/videos`, {
            params: { id, nextPageToken, q, accountId },
        })
        .then(getData);
};

const deleteAdAccounts = (token, id) => {
    return axios.delete(`${BASE}/${id}/?token=${token}`).then(getData);
};

const getAsset = (queries = {}) => {
    return axios.get(`${LINK}/assets?${qs.stringify(queries)}`).then(getData);
};

const getConnectionTypes = memoize(
    ({
        workspaceId,
        searchTerm,
        size = 50,
        page = 0,
        filtersIds,
        isFromAdgen = false,
    }) => {
        return axios
            .get(`${CONNECTION}/types`, {
                params: {
                    workspaceId: workspaceId || undefined,
                    searchTerm,
                    size,
                    page,
                    filtersIds,
                    ...(isFromAdgen && { isFromAdgen }),
                },
            })
            .then(getData);
    },
    {
        normalizer: JSON.stringify,
    }
);

const getConnectionOptions = ({ id, token }) => {
    return axios
        .get(`${CONNECTION}/types/options/${id}`, { params: { token } })
        .then(getData);
};

const addConnections = ({
    workspaceId,
    connectionTypeId,
    options,
    token,
    id,
}) => {
    const apiEndPoint = token
        ? `${CONNECTION}?token=${token}`
        : `${CONNECTION}`;

    return axios
        .post(apiEndPoint, {
            workspaceId: workspaceId || undefined,
            connectionTypeId,
            options,
            id,
        })
        .then(getData);
};

const addAuthConnection = ({ id, options, token }) => {
    return axios
        .post(`${CONNECTION}/${id}/authorize?token=${token}`, options)
        .then(getData);
};

const getConnections = ({ workspaceId, connectionTypesIds, token }) => {
    return axios
        .get(`${CONNECTION}`, {
            params: {
                workspaceId: workspaceId || undefined,
                connectionTypeIds: connectionTypesIds,
                // token,
            },
        })
        .then(getData);
};

const deleteConnection = (id) => {
    return axios.delete(`${CONNECTION}/${id}`).then(getData);
};

const getDatastreamTypes = ({
    workspaceId,
    searchTerm,
    size = 50,
    connectionId,
    page,
    source,
    connectorType,
    token,
    isFromAdgen = false,
}) => {
    return axios
        .get(`${DATASTREAM}/types`, {
            params: {
                workspaceId: workspaceId || undefined,
                searchTerm: searchTerm || undefined,
                size,
                connectionId,
                page,
                source,
                connectorType,
                token,
                ...(isFromAdgen && { isFromAdgen }),
            },
        })
        .then(getData);
};

const getDatastreamOptions = ({ datastreamTypeId, token, connectionId }) => {
    return axios
        .get(`${DATASTREAM}/types/options/${datastreamTypeId}`, {
            params: { token, connectionId },
        })
        .then(getData);
};

const getDynamicChoices = ({
    inputData,
    lookupUrl,
    connectionId,
    connectionTypeId,
    token,
}) => {
    return axios
        .post(`${DATASTREAM}/dynamicChoicesValues?token=${token}`, {
            inputData,
            lookupUrl,
            connectionId,
            ...(connectionTypeId && { connectionTypeId }),
        })
        .then(getData);
};

const addDatastream = ({
    workspaceId,
    datastreamTypeId,
    options,
    connectionId,
    token,
    isFromAdgen = false,
    teamspaceIds,
}) => {
    return axios
        .post(`${DATASTREAM}/v2`, {
            workspaceId: workspaceId || undefined,
            datastreamTypeId,
            options,
            connectionId,
            token,
            ...(isFromAdgen && { isFromAdgen }),
            ...(teamspaceIds?.length && { teamspaceIds }),
        })
        .then(getData);
};

const newDeleteDatastream = ({ id, token }) =>
    axios
        .post(`${DATASTREAM}/disconnectDataSource/${id}?token=${token}`)
        .then(getData);

const getLookerStudioRediectUrl = () => {
    return axios.post('/lookerstudio/redirect').then(getData);
};

const postReconnectAsset = (id) => {
    return axios.post(`${BASE}/${id}/reconnect`).then(getData);
};

const getOAuthRedirectUrl = ({ id, token }) => {
    return axios
        .get(`${CONNECTION}/${id}/authorizeUrl?token=${token}`)
        .then(getData);
};

const generateDataSourceToken = (id) => {
    return axios.post(`dashboard/dataSource/${id}/token`).then(getData);
};

const reconnectDataSourceWithToken = (token) => {
    return axios.post(`${BASE}/reconnect`, { token }).then(getData);
};

const getAssetWithToken = (token) => {
    return axios.get(`${BASE}/public?token=${token}`).then(getData);
};

export default {
    list,
    addAssets,
    toggleActive,
    getAssets,
    facebookEvents,
    getYouTubeVideoSearch,
    deleteAdAccounts,
    getAsset,
    getConnectionTypes,
    getConnectionOptions,
    addAuthConnection,
    addConnections,
    getConnections,
    deleteConnection,
    getDatastreamTypes,
    getDatastreamOptions,
    getDynamicChoices,
    addDatastream,
    newDeleteDatastream,
    getLookerStudioRediectUrl,
    postReconnectAsset,
    getOAuthRedirectUrl,
    generateDataSourceToken,
    reconnectDataSourceWithToken,
    getAssetWithToken,
};
